/**
 * projectileGeometry — the caster's FINAL projectile, described once for
 * every screen (plan 29 F5 follow-up, lane BY 2026-10-11).
 *
 * Production boot registers only the local player's class, so an observer
 * used to rebuild another class's projectile ability from TOML: Multiple
 * Arrows' TOML has no projectile at all (the observer drew a cast puff and no
 * arrows), and every hand-built projectile differed in shape, size, speed,
 * homing, lifespan, trail or muzzle flash (Fireball 20×20 sphere at 600 px/s
 * remotely vs the caster's 30×30 circle at 400 px/s).
 *
 * `projectileGeometryOf` reads the registered metadata the caster's
 * ProjectileBehavior / MultiProjectileBehavior fire from (with their own
 * fallbacks resolved), so the description is the caster's pixels. It rides
 * `castGeometry.projectile` into the generated snapshot (`make
 * cast-geometry`); the observer launches `remoteShots` from it — the same
 * count, lanes, box, speed and look, damage 0 (presentation only).
 */
import { PROJECTILE_VFX } from '../../../shared/config/projectile-vfx.config';
import { GameConstants } from '../../../shared/GameConstants';

export interface ProjectileGeometry {
  /** The fire time after the cast starts (metadata.castTime). */
  windUpMs: number;
  /** 'basic' for a single shot, 'special' for a volley (the local behaviours' type). */
  type: 'basic' | 'special';
  shape: string;
  color: number;
  speed: number;
  width: number;
  height: number;
  rotationSpeed: number;
  gravity: number;
  lifespan: number;
  homing: { strength: number; maxTurnRate: number; delay: number };
  /** Total vertical span of a volley's parallel lanes (MultiProjectileBehavior). */
  spreadY: number;
  trajectory?: string;
  trajectoryAmplitude?: number;
  trajectoryFrequency?: number;
  /** The ribbon trail def (`metadata.vfx.trail`); absent → ProjectileSystem's `${id}_trail` convention. */
  trailEffectId?: string;
  /** The muzzle flash at the spawn point (a volley's, once with its first shot). */
  muzzle?: { effectId: string; color: number; duration: number; scale: number };
  /** A splash drawn where it lands (`aoeOnImpact`), exactly `radius` wide — Explosive Shot. */
  impactBlast?: { radius: number; effectId: string };
}

interface ProjectileSource {
  castTime?: number;
  projectile?: {
    speed?: number; shape?: string; color?: number; width?: number; height?: number; rotationSpeed?: number;
    gravity?: number; lifespan?: number; spreadY?: number; trajectory?: string; trajectoryAmplitude?: number;
    trajectoryFrequency?: number; homing?: { strength?: number; maxTurnRate?: number; delay?: number };
    modifiers?: { aoeOnImpact?: { radius: number; vfxId?: string } };
  };
  vfx?: { muzzleFlash?: string; color?: number; duration?: number; scale?: number; trail?: string };
}

/**
 * The caster's projectile as ProjectileBehavior (single) or
 * MultiProjectileBehavior (`volley`) launches it. Undefined without
 * projectile metadata.
 */
export function projectileGeometryOf(meta: ProjectileSource, volley?: { count: number } | null): ProjectileGeometry | undefined {
  const p = meta.projectile;
  if (!p) return undefined;
  const multi = !!volley;
  const box = multi ? PROJECTILE_VFX.multi : PROJECTILE_VFX.single;
  const g: ProjectileGeometry = {
    windUpMs: meta.castTime ?? 0,
    type: multi ? 'special' : 'basic',
    shape: p.shape ?? (multi ? 'star' : 'rect'),
    color: p.color ?? 0xFFFFFF,
    speed: p.speed ?? 800,
    width: p.width ?? box.width,
    height: p.height ?? box.height,
    rotationSpeed: p.rotationSpeed ?? 0,
    gravity: p.gravity ?? 0,
    lifespan: p.lifespan ?? GameConstants.Combat.PROJECTILE_LIFESPAN,
    homing: {
      strength: p.homing?.strength ?? 0,
      maxTurnRate: p.homing?.maxTurnRate ?? GameConstants.Combat.HOMING_MAX_TURN_RATE,
      delay: p.homing?.delay ?? (multi ? GameConstants.Combat.HOMING_DELAY_SPECIAL : 0),
    },
    spreadY: multi ? (p.spreadY ?? 0) : 0,
  };
  if (!multi && p.trajectory) {
    g.trajectory = p.trajectory;
    if (p.trajectoryAmplitude !== undefined) g.trajectoryAmplitude = p.trajectoryAmplitude;
    if (p.trajectoryFrequency !== undefined) g.trajectoryFrequency = p.trajectoryFrequency;
  }
  if (!multi && meta.vfx?.trail) g.trailEffectId = meta.vfx.trail;
  const blast = p.modifiers?.aoeOnImpact;
  if (!multi && blast?.vfxId) g.impactBlast = { radius: blast.radius, effectId: blast.vfxId };
  if (meta.vfx?.muzzleFlash) {
    g.muzzle = {
      effectId: meta.vfx.muzzleFlash,
      color: meta.vfx.color ?? 0xFFFFFF,
      duration: meta.vfx.duration ?? 100,
      scale: meta.vfx.scale ?? 1,
    };
  }
  return g;
}

/** A volley lane's vertical offset: `count` parallel lanes evenly over `spreadY`, centred. */
export function volleyLaneOffset(index: number, count: number, spreadY: number): number {
  return count > 1 ? ((index / (count - 1)) - 0.5) * spreadY : 0;
}

/** Where one shot leaves and how fast (ProjectileAimResolver's launch). */
export interface ShotLaunch { spawnX: number; spawnY: number; vx: number; vy: number; direction: 'left' | 'right' }

/**
 * One presentation-only shot of the caster's projectile: the observer's copy
 * of what the caster's behaviour created (damage 0 — the server's broadcast
 * owns damage; the projectile system plays a visual-only impact).
 */
export function remoteShot(
  g: ProjectileGeometry,
  launch: ShotLaunch,
  shot: { index: number; count: number; ownerId: string; targetId: string | null; abilityId: string },
): Record<string, unknown> {
  const config: Record<string, unknown> = {
    x: launch.spawnX,
    y: launch.spawnY + volleyLaneOffset(shot.index, shot.count, g.spreadY),
    ownerId: shot.ownerId,
    abilityId: shot.abilityId,
    damage: 0,
    targetId: shot.targetId,
    type: g.type,
    direction: launch.direction,
    initialVx: launch.vx,
    initialVy: launch.vy,
    shape: g.shape,
    color: g.color,
    speed: g.speed,
    width: g.width,
    height: g.height,
    rotationSpeed: g.rotationSpeed,
    gravity: g.gravity,
    startTime: Date.now(),
    homingStrength: g.homing.strength,
    maxTurnRate: g.homing.maxTurnRate,
    homingDelay: g.homing.delay,
    lifespan: g.lifespan,
  };
  if (g.trajectory) {
    config.trajectory = g.trajectory;
    config.trajectoryAmplitude = g.trajectoryAmplitude;
    config.trajectoryFrequency = g.trajectoryFrequency;
  }
  if (g.trailEffectId) config.trailVfxId = g.trailEffectId;
  // Presentation only: a zero-damage splash draws its effect and requests nothing
  // (handleAoeOnImpact skips the area request for a visual-only projectile).
  if (g.impactBlast) config.aoeOnImpact = { radius: g.impactBlast.radius, damagePercent: 0, vfxId: g.impactBlast.effectId };
  return config;
}
